import axios from 'axios';
import ApiRoute from '../../utils/apiRoutes';
import { SOURCE } from '../../utils/constants';

const axiosApiInstance = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL });

// Separate instance for auth requests (no interceptors)
const axiosAuthInstance = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL });

// Token refresh state management
let isRefreshing = false;
let failedRequestsQueue = [];

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
    failedRequestsQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedRequestsQueue = [];
};

// Clear auth storage and redirect to login when session expires
function clearAuthStorageAndLogout() {
    localStorage.removeItem('profile');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('firstLogin');
    localStorage.removeItem('loginTimeStamp');

    // Redirect to login page
    if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
    }
}

// Request interceptor for API calls
axiosApiInstance.interceptors.request.use(
    async config => {
        const token = localStorage.getItem('token');
        const profile = localStorage.getItem('profile');
        config.headers = {
            ...config.headers,
            'x-auth-token': token,
            'x-role-name': profile,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }
        return config;
    },
    error => {
        Promise.reject(error)
    }
);

// Response interceptor for API calls
axiosApiInstance.interceptors.response.use(
    (response) => {
        return response
    },
    async function (error) {
        const originalRequest = error.config;

        if (!error.response) {
            return Promise.reject(error);
        }

        // Handle 401 Unauthorized errors
        if (error.response.status === 401 && !originalRequest._retry) {
            console.log('401 error detected, attempting token refresh');
            // Check if we have a refresh token
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
                clearAuthStorageAndLogout();
                return Promise.reject(error);
            }

            // If already refreshing, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedRequestsQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers['x-auth-token'] = token;
                    return axiosApiInstance(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await axiosAuthInstance.post(ApiRoute.getAccessToken, { refreshToken });

                // Check for token in response (handle different response structures)
                const newToken = response?.data?.token || response?.data?.accessToken;

                if (newToken) {
                    const tokenString = newToken.toString();
                    localStorage.setItem('token', tokenString);
                    localStorage.setItem('loginTimeStamp', new Date().toISOString());

                    // Process queued requests with new token
                    processQueue(null, tokenString);

                    // Retry the original request with new token
                    originalRequest.headers['x-auth-token'] = tokenString;
                    return axiosApiInstance(originalRequest);
                } else {
                    // No token in response - refresh failed
                    processQueue(new Error('Token refresh failed'), null);
                    clearAuthStorageAndLogout();
                    return Promise.reject(error);
                }
            } catch (refreshError) {
                // Refresh token expired/invalid - clear storage and logout
                processQueue(refreshError, null);
                clearAuthStorageAndLogout();
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// auth endpoints start

export async function checkUserExistence(phoneNumber) {
    const query = `?phoneNumber=${phoneNumber}&source=${SOURCE.WEBSITE}`;
    return await axiosAuthInstance.get(ApiRoute.checkUserExistence + query);
}

export async function verifyOTP(data) {
    return await axiosAuthInstance.post(ApiRoute.verifyOTP, { ...data, source: SOURCE.WEBSITE });
}

export async function checkUserProfiles() {
    const query = `?source=${SOURCE.WEBSITE}`;
    return await axiosApiInstance.get(ApiRoute.checkUserProfiles + query);
}

export async function getUserDetails() {
    const query = `?source=${SOURCE.WEBSITE}`;
    return await axiosApiInstance.get(ApiRoute.getUserDetails + query);
}

export async function getAccessToken(data) {
    return await axiosAuthInstance.post(ApiRoute.getAccessToken, data);
}

// auth endpoints end

// student apis start

export async function getStudents(query = "") {
    return await axiosApiInstance.get(ApiRoute.getStudents + query);
}

export async function getStudentById(id) {
    return await axiosApiInstance.get(ApiRoute.getStudentById.replace(":id", id));
}

export async function createStudent(data) {
    return await axiosApiInstance.post(ApiRoute.createStudent, data);
}

export async function updateStudent(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateStudent.replace(":id", id), data);
}

export async function deleteStudent(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteStudent.replace(":id", id));
}

export async function restoreStudent(id) {
    return await axiosApiInstance.patch(ApiRoute.restoreStudent.replace(":id", id));
}

// student apis end

// attenadance apis start

export async function getAttendanceByStudentId(query = "") {
    return await axiosApiInstance.get(ApiRoute.getAttendance + query);
}

export async function createAttendance(data) {
    return await axiosApiInstance.post(ApiRoute.createAttendance, data);
}

export async function updateAttendance(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateAttendance.replace(":id", id), data);
}

export async function deleteAttendance(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteAttendance.replace(":id", id));
}

// attenadance apis end

// driver apis start

export async function getDrivers(query = "") {
    return await axiosApiInstance.get(ApiRoute.getDrivers + query);
}

export async function getDriverById(id) {
    return await axiosApiInstance.get(ApiRoute.getDriverById.replace(":id", id));
}

export async function createDriver(data) {
    return await axiosApiInstance.post(ApiRoute.createDriver, data);
}

export async function updateDriver(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateDriver.replace(":id", id), data);
}

export async function deleteDriver(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteDriver.replace(":id", id));
}

export async function restoreDriver(id) {
    return await axiosApiInstance.patch(ApiRoute.restoreDriver.replace(":id", id));
}

// driver apis end

// agent apis start

export async function getAgents(query = "") {
    return await axiosApiInstance.get(ApiRoute.getAgents + query);
}
export async function getAgentById(id) {
    return await axiosApiInstance.get(ApiRoute.getAgentById.replace(":id", id));
}


export async function createAgent(data) {
    return await axiosApiInstance.post(ApiRoute.createAgent, data);
}

export async function updateAgent(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateAgent.replace(":id", id), data);
}

export async function deleteAgent(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteAgent.replace(":id", id));
}

// agent apis end


// organization apis start

export async function getOrganizations(query = "") {
    return await axiosApiInstance.get(ApiRoute.getOrganizations + query);
}

export async function getOrganizationById(id) {
    return await axiosApiInstance.get(ApiRoute.getOrganizationById.replace(":id", id));
}

export async function createOrganization(data) {
    return await axiosApiInstance.post(ApiRoute.createOrganization, data);
}

export async function organizationSignup(data) {
    return await axiosAuthInstance.post(ApiRoute.organizationSignup, data);
}


export async function updateOrganization(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateOrganization.replace(":id", id), data);
}

export async function approveOrganization(id) {
    return await axiosApiInstance.patch(ApiRoute.approveOrganization.replace(":id", id));
}

// export async function deleteOrganization(id) {
//     return await axios.delete(role + ApiRoute.deleteOrganization.replace(":id", id));
// }

// organization apis end



// Videos apis start

export async function getVideos(query = "") {
    return await axiosApiInstance.get(ApiRoute.getVideos + query);
}

export async function createVideo(data) {
    return await axiosApiInstance.post(ApiRoute.createVideo, data);
}

export async function updateVideo(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateVideo.replace(":id", id), data);
}

export async function deleteVideo(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteVideo.replace(":id", id));
}

// Video apis end


// Bookings apis start

export async function getBookings(query = "") {
    return await axiosApiInstance.get(ApiRoute.getBookings + query);
}

// export async function getBookingById(id) {
//     return await axios.get(ApiRoute.getBookingById.replace(":id", id));
// }


// Bookings apis end


// Commissions apis start

export async function getCommissions(query = "") {
    return await axiosApiInstance.get(ApiRoute.getCommissions + query);
}

// export async function getCommissionById(id) {
//     return await axios.get(ApiRoute.getCommissions.replace(":id", id));
// }


// Commissions apis end

// Referrals apis start

export async function getReferrals(query = "") {
    return await axiosApiInstance.get(ApiRoute.getReferrals + query);
}

export async function getReferralById(id) {
    return await axiosApiInstance.get(ApiRoute.getReferralById.replace(":id", id));
}

export async function markReferralPayment(id, data) {
    return await axiosApiInstance.patch(ApiRoute.markReferralPayment.replace(":id", id), data);
}

// Referrals apis end

// Payments apis start

export async function getPayments(query = "") {
    return await axiosApiInstance.get(ApiRoute.getPayments + query);
}

export async function getPaymentById(id) {
    return await axiosApiInstance.get(ApiRoute.getPaymentById.replace(":id", id));
}

export async function createPayment(data) {
    return await axiosApiInstance.post(ApiRoute.createPayment, data);
}

export async function updatePayment(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updatePayment.replace(":id", id), data);
}

export async function deletePayment(id) {
    return await axiosApiInstance.delete(ApiRoute.deletePayment.replace(":id", id));
}

// Payments apis end

// Receipts apis start

export async function getReceipts(query = "") {
    return await axiosApiInstance.get(ApiRoute.getReceipts + query);
}

export async function getReceiptById(id) {
    return await axiosApiInstance.get(ApiRoute.getReceiptById.replace(":id", id));
}

// Receipts apis end

// Video categories apis start

export async function getVideoCategories(query = "") {
    return await axiosApiInstance.get(ApiRoute.getVideoCategories + query);
}


export async function createVideoCategory(data) {
    return await axiosApiInstance.post(ApiRoute.createVideoCategory, data);
}

export async function updateVideoCategory(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateVideoCategory.replace(":id", id), data);
}

export async function deleteVideoCategory(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteVideoCategory.replace(":id", id));
}

// Video categories apis end


// Enquiry apis start

export async function getEnquiries(query = "") {
    return await axiosApiInstance.get(ApiRoute.getEnquiries + query);
}

export async function createEnquiry(data) {
    return await axiosApiInstance.post(ApiRoute.createEnquiry, data);
}

export async function updateEnquiry(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateEnquiry.replace(":id", id), data);
}

export async function deleteEnquiry(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteEnquiry.replace(":id", id));
}

// Enquiry apis end


// Service apis start

export async function getServices(query = "") {
    return await axiosApiInstance.get(ApiRoute.getServices + query);
}

export async function createService(data) {
    return await axiosApiInstance.post(ApiRoute.createService, data);
}

export async function updateService(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateService.replace(":id", id), data);
}

export async function deleteService(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteService.replace(":id", id));
}

// Service apis end

// Invoice apis start

export async function createInvoice(data) {
    return await axiosApiInstance.post(ApiRoute.createInvoice, data);
}

export async function getInvoice(query = "") {
    return await axiosApiInstance.get(ApiRoute.getInvoice + query);
}

export async function addInvoicePayment(invoiceId, data) {
    return await axiosApiInstance.post(ApiRoute.addInvoicePayment.replace(":id", invoiceId), data);
}

export async function updateInvoicePayment(invoiceId, paymentId, data) {
    return await axiosApiInstance.patch(`/invoice/${invoiceId}/payment/${paymentId}`, data);
}

export async function getRecentInvoices() {
    return await axiosApiInstance.get(ApiRoute.getRecentInvoices);
}

export async function updateInvoice(invoiceId, data) {
    return await axiosApiInstance.patch(ApiRoute.updateInvoice.replace(":id", invoiceId), data);
}

// Invoice apis end


// Expenses apis start

export async function getExpenses(query = "") {
    return await axiosApiInstance.get(ApiRoute.getExpenses + query);
}

export async function getExpenseSummary(query = "") {
    return await axiosApiInstance.get(ApiRoute.getExpenseSummary + query);
}

export async function getExpenseById(id) {
    return await axiosApiInstance.get(ApiRoute.getExpenseById.replace(":id", id));
}

export async function createExpense(data) {
    return await axiosApiInstance.post(ApiRoute.createExpense, data);
}

export async function updateExpense(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateExpense.replace(":id", id), data);
}

export async function deleteExpense(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteExpense.replace(":id", id));
}

// Expenses apis end


// admin endpoints start

export async function getAdmins() {
    return await axiosApiInstance.get(ApiRoute.getAdmins);
}

export async function createAdmin(data) {
    return await axiosApiInstance.post(ApiRoute.createAdmin, data);
}

export async function updateAdmin(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateAdmin.replace(":id", id), data);
}

export async function deleteAdmin(data) {
    return await axiosApiInstance.post(ApiRoute.deleteAdmin, data);
}

// admin endpoints end


// contactUs endpoints start

export async function sendMessage(data) {
    return await axiosAuthInstance.post(ApiRoute.sendMessage, data);
}

export async function getContactUs(query = "") {
    return await axiosApiInstance.get(ApiRoute.getContactUs + query);
}

export async function updateContactUs(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateContactUs.replace(":id", id), data);
}

export async function deleteContactUs(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteContactUs.replace(":id", id));
}


// contact Us endpoints ends

// dealer apis start

export async function getDealers(query = "") {
    return await axiosApiInstance.get(ApiRoute.getDealers + query);
}

export async function getDealerById(id) {
    return await axiosApiInstance.get(ApiRoute.getDealerById.replace(":id", id));
}

export async function createDealer(data) {
    return await axiosApiInstance.post(ApiRoute.createDealer, data);
}

export async function updateDealer(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateDealer.replace(":id", id), data);
}

export async function deleteDealer(id) {
    return await axiosApiInstance.post(ApiRoute.deleteDealer.replace(":id", id));
}

// dealer apis end


// car apis start

export async function getCars(query = "") {
    return await axiosApiInstance.get(ApiRoute.getCars + query);
}

export async function getCarById(id) {
    return await axiosApiInstance.get(ApiRoute.getCarById.replace(":id", id));
}

export async function createCar(data) {
    return await axiosApiInstance.post(ApiRoute.createCar, data);
}

export async function updateCar(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateCar.replace(":id", id), data);
}

export async function deleteCar(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteCar.replace(":id", id));
}

// car apis end

// pre-signed url apis start

export async function getPreSignedUrl(data) {
    return await axiosApiInstance.post(ApiRoute.getPreSignedUrl, data);
}

// pre-signed url apis end

// fuel card apis start

export async function createFuelCardApplication(data) {
    return await axiosApiInstance.post(ApiRoute.createFuelCardApplication, data);
}

export async function getFuelCardApplications(query = "") {
    return await axiosApiInstance.get(ApiRoute.getFuelCardApplications + query);
}

export async function getFuelCardApplicationByOrg(organizationId) {
    return await axiosApiInstance.get(ApiRoute.getFuelCardApplicationByOrg.replace(':organizationId', organizationId));
}

export async function updateFuelCardApplication(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateFuelCardApplication.replace(':id', id), data);
}

export async function resubmitFuelCardApplication(data) {
    return await axiosApiInstance.patch(ApiRoute.resubmitFuelCardApplication, data);
}

// fuel card apis end

// batch apis start

export async function createBatch(data) {
    return await axiosApiInstance.post(ApiRoute.createBatch, data);
}

export async function getBatches(query = "") {
    return await axiosApiInstance.get(ApiRoute.getBatches + query);
}

export async function getBatchById(id) {
    return await axiosApiInstance.get(ApiRoute.getBatchById.replace(":id", id));
}

export async function updateBatch(id, data) {
    return await axiosApiInstance.patch(ApiRoute.updateBatch.replace(":id", id), data);
}

export async function deleteBatch(id) {
    return await axiosApiInstance.delete(ApiRoute.deleteBatch.replace(":id", id));
}

export async function assignStudentToBatch(batchId, studentId) {
    return await axiosApiInstance.put(
        ApiRoute.assignStudentToBatch.replace(":batchId", batchId).replace(":studentId", studentId)
    );
}

export async function removeStudentFromBatch(batchId, studentId) {
    return await axiosApiInstance.delete(
        ApiRoute.removeStudentFromBatch.replace(":batchId", batchId).replace(":studentId", studentId)
    );
}

// batch apis end

// public apis start

export async function getPublicOrganizations(query = "") {
    return await axiosAuthInstance.get(ApiRoute.getPublicOrganizations + query);
}

export async function getPublicDealers(query = "") {
    return await axiosAuthInstance.get(ApiRoute.getPublicDealers + query);
}

// public apis end